import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useEffect, useState } from 'react';
import { ChatHeader } from '../components/ChatHeader';
import { ChatWelcome } from '../components/ChatWelcome';
import { Composer } from '../components/Composer';
import { MessageThread } from '../components/MessageThread';
import { PromptLibrary } from '../components/PromptLibrary';
import { useConversation } from '../hooks/useConversation';
import type { Prefill } from '../hooks/usePrefilledText';
import { useAssistantStore } from '../model/assistantStore';
import { useChatStore } from '../model/chatStore';
import type { PromptCategory } from '../model/prompts';

const THREAD_WIDTH = 760;
const HOME_WIDTH = 640;

/** Ask Research, the home page: a greeting and prompts before the first question, then the chat. */
export function AskPage() {
  const { turns, send, stop, isBusy } = useConversation();
  const takeQueued = useAssistantStore((s) => s.takeQueued);
  const startNew = useChatStore((s) => s.startNew);
  const title = useChatStore((s) => s.conversations.find((c) => c.id === s.activeId)?.title ?? '');
  // undefined: closed; null: open on all prompts; a category: open on that category.
  const [library, setLibrary] = useState<PromptCategory | null | undefined>(undefined);
  const openLibrary = () => { setLibrary(null); };
  // A picked prompt goes into the box to edit, not straight to the assistant.
  const [prefill, setPrefill] = useState<Prefill | null>(null);
  const fillPrompt = (text: string) => { setPrefill((p) => ({ text, id: (p?.id ?? 0) + 1 })); };
  const isEmpty = turns.length === 0;

  // A question handed over from a report page ("Ask about this report") opens in a new chat.
  useEffect(() => {
    const q = takeQueued();
    if (!q) return;
    startNew();
    send(q);
    // Runs once on arrival; send is recreated every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const composer = (
    <Composer
      key={isEmpty ? 'new' : 'chat'}
      isBusy={isBusy}
      prefill={prefill}
      onSend={(q) => { setPrefill(null); send(q); }}
      onStop={stop}
      onOpenLibrary={openLibrary}
      placeholder={isEmpty ? undefined : 'Ask a follow-up'}
      focusOnMount
    />
  );

  return (
    // Desktop hides the top bar on this page; on phones its 1px bottom border sits outside its height.
    <Stack component="section" aria-label="Ask Research" sx={{ height: { xs: 'calc(100dvh - 61px)', md: '100dvh' } }}>
      {isEmpty ? (
        <Box sx={{ flexGrow: 1, overflowY: 'auto', display: 'flex', alignItems: { md: 'center' }, px: 2, py: { xs: 4, md: 6 } }}>
          <Box sx={{ width: '100%', maxWidth: HOME_WIDTH, mx: 'auto' }}>
            <ChatWelcome composer={composer} onPrompt={fillPrompt} onSeeAll={setLibrary} />
          </Box>
        </Box>
      ) : (
        <>
          <ChatHeader title={title} onOpenLibrary={openLibrary} />
          <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 2, pt: { xs: 2.5, md: 3.5 }, pb: 2 }}>
            <Box sx={{ maxWidth: THREAD_WIDTH, mx: 'auto' }}>
              <MessageThread turns={turns} onAsk={send} />
            </Box>
          </Box>
          <Box sx={{ px: 2, pb: 2.5, pt: 1, maxWidth: THREAD_WIDTH + 32, width: '100%', mx: 'auto' }}>{composer}</Box>
        </>
      )}
      <PromptLibrary open={library !== undefined} initialCategory={library ?? null} onClose={() => { setLibrary(undefined); }} onPick={fillPrompt} />
    </Stack>
  );
}
